import { useEffect, useRef, useState, useSyncExternalStore, type PointerEvent, type ReactNode } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionValue,
} from 'motion/react';
import type { GameLayer } from '@b1fr0st/contracts/game-layers';
import { observeScrollTile, remeasure } from './layeredTileScroll.js';
import { hasPopoutLayer } from './tileArt.js';

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
export const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

const SPRING = { stiffness: 170, damping: 22, mass: 0.6 };
/** Layer travel at the full depth on a 380px-wide tile; smaller tiles travel proportionally less. */
const BASE_DEPTH_PX = 16;
const REFERENCE_WIDTH_PX = 380;
const MIN_SIZE_FACTOR = 0.45;
const TILT_DEG = 8;
const POP_RISE_PCT = 12;
const HOVER_LIFT = 1.03;
const PRESS_SCALE = 0.95;

function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
    () => false,
  );
}

function depthPxFor(width: number): number {
  return BASE_DEPTH_PX * Math.max(width / REFERENCE_WIDTH_PX, MIN_SIZE_FACTOR);
}

/** Class of the looping keyframe a layer plays while its tile is active (globals.css). */
export function layerMotionClass(layer: GameLayer): string {
  return layer.motion ? `layer-motion-${layer.motion}` : '';
}

interface LayerImgProps {
  layer: GameLayer;
  animated: boolean;
  active: boolean;
  x: MotionValue<number>;
  y: MotionValue<number>;
  depthPx: MotionValue<number>;
  pop: MotionValue<number>;
}

function LayerImg({ layer, animated, active, x, y, depthPx, pop }: LayerImgProps) {
  const transform = useTransform([x, y, depthPx, pop], ([px, py, d, p]: number[]) => {
    const dx = -px * d * layer.depth;
    const dy = -py * d * layer.depth;
    const rise = layer.popout ? POP_RISE_PCT * p : 0;
    const base = `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0)`;
    return rise > 0.01 ? `${base} translateY(${(-rise).toFixed(2)}%) scale(${(1 + rise / 100).toFixed(4)})` : base;
  });
  return (
    <motion.img
      src={layer.url}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      className={`absolute h-auto max-w-none select-none pointer-events-none ${active ? layerMotionClass(layer) : ''}`}
      style={{
        left: `${layer.left ?? 0}%`,
        top: `${layer.top ?? 0}%`,
        width: `${layer.width ?? 100}%`,
        transform: animated ? transform : undefined,
        transformOrigin: layer.popout ? '50% 100%' : undefined,
        willChange: animated ? 'transform' : undefined,
      }}
    />
  );
}

interface LayeredTileProps {
  layers: GameLayer[];
  /** Accessible name for the art, as the single-image tile gives it. */
  label: string;
  /** Corner radius of the tile, matching its container's rounding. */
  radius: string;
  /** Draw the stack without any movement (locked tiles, tiny thumbnails). */
  still?: boolean;
  /** Overlays (scrim, badges, name) drawn above the art and tilted with it. */
  children?: ReactNode;
}

/**
 * A game tile's art drawn as stacked layers that shift by depth: with the
 * pointer on mouse devices, with the page scroll on touch screens (see
 * layeredTileScroll.ts), not at all under reduced motion.
 *
 * Layer 0 sits in the rounded frame and is always clipped. Every later layer
 * that is not `popout` gets its own clipped wrapper rather than sharing the
 * frame, so the stack keeps its back-to-front order even when a clipped layer
 * sits in front of a popout one. Popout layers are clipped only by the tile's
 * clip-path, which leaves the top and sides open and keeps the bottom edge.
 */
export function LayeredTile({ layers, label, radius, still = false, children }: LayeredTileProps) {
  const reduced = useMediaQuery(REDUCED_MOTION_QUERY);
  const finePointer = useMediaQuery(FINE_POINTER_QUERY);
  const mode = still || reduced ? 'static' : finePointer ? 'pointer' : 'scroll';
  const animated = mode !== 'static';
  const popout = hasPopoutLayer(layers);

  const rootRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [centred, setCentred] = useState(false);
  const [pressed, setPressed] = useState(false);
  const active = animated && (hovered || centred || pressed);

  const tx = useMotionValue(0);
  const ty = useMotionValue(0);
  const popTarget = useMotionValue(0);
  const liftTarget = useMotionValue(1);
  const depthPx = useMotionValue(depthPxFor(REFERENCE_WIDTH_PX * MIN_SIZE_FACTOR));
  const x = useSpring(tx, SPRING);
  const y = useSpring(ty, SPRING);
  const pop = useSpring(popTarget, SPRING);
  const lift = useSpring(liftTarget, SPRING);

  useEffect(() => {
    popTarget.set(active ? 1 : 0);
  }, [active, popTarget]);

  useEffect(() => {
    liftTarget.set(mode === 'pointer' && hovered ? HOVER_LIFT : mode === 'scroll' && pressed ? PRESS_SCALE : 1);
  }, [mode, hovered, pressed, liftTarget]);

  useEffect(() => {
    const el = rootRef.current;
    if (mode !== 'scroll' || !el) return;
    depthPx.set(depthPxFor(el.offsetWidth));
    const stop = observeScrollTile({ el, onOffset: (v) => ty.set(v), onCentred: setCentred });
    return () => {
      stop();
      ty.set(0);
      setCentred(false);
    };
  }, [mode, depthPx, ty]);

  const tileTransform = useTransform([x, y, lift], ([px, py, l]: number[]) =>
    mode === 'pointer'
      ? `perspective(900px) rotateX(${(py * TILT_DEG).toFixed(2)}deg) rotateY(${(-px * TILT_DEG).toFixed(2)}deg) scale(${l.toFixed(4)})`
      : `scale(${l.toFixed(4)})`,
  );
  const shineX = useTransform(x, (v) => 50 + v * 50);
  const shineY = useTransform(y, (v) => 50 + v * 50);
  const shine = useMotionTemplate`radial-gradient(circle at ${shineX}% ${shineY}%, rgba(255,255,255,.75), rgba(255,255,255,0) 45%)`;

  function follow(e: PointerEvent<HTMLDivElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    tx.set(Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1)));
    ty.set(Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1)));
  }

  const handlers =
    mode === 'pointer'
      ? {
          onPointerEnter: (e: PointerEvent<HTMLDivElement>) => {
            depthPx.set(depthPxFor(e.currentTarget.getBoundingClientRect().width));
            setHovered(true);
            follow(e);
          },
          onPointerMove: follow,
          onPointerLeave: () => {
            setHovered(false);
            tx.set(0);
            ty.set(0);
          },
        }
      : mode === 'scroll'
        ? (() => {
            const release = () => {
              setPressed(false);
              remeasure();
            };
            return {
              onPointerDown: () => setPressed(true),
              onPointerUp: release,
              onPointerCancel: release,
              onPointerLeave: release,
            };
          })()
        : {};

  const clipPath = popout ? `inset(-45% -30% 0 -30% round 0 0 ${radius} ${radius})` : `inset(0 round ${radius})`;

  return (
    <motion.div
      ref={rootRef}
      data-layered-tile=""
      className={`absolute inset-0 rounded-[inherit] ${active ? 'z-10' : ''}`}
      style={{ clipPath, transform: animated ? tileTransform : undefined }}
      {...handlers}
    >
      {layers.map((layer, i) => {
        const img = (
          <LayerImg key={i} layer={layer} animated={animated} active={active} x={x} y={y} depthPx={depthPx} pop={pop} />
        );
        if (layer.popout && i > 0) return img;
        if (i === 0) {
          return (
            <div
              key={i}
              data-tile-frame=""
              className="absolute inset-0 overflow-hidden rounded-[inherit] bg-[#07060f]"
              role="img"
              aria-label={label}
            >
              {img}
            </div>
          );
        }
        return (
          <div key={i} className="absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
            {img}
          </div>
        );
      })}
      {mode === 'pointer' && (
        <motion.div
          aria-hidden
          className={`pointer-events-none absolute inset-0 rounded-[inherit] mix-blend-soft-light transition-opacity duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: shine }}
        />
      )}
      {children}
    </motion.div>
  );
}
